'use strict';

define('vb/private/stateManagement/context/withEnvContext',[],() => {
  /**
   * Internal support for the `$withEnv(...)` expression helper.
   *
   * `$withEnv` returns a short-lived proxy over the normal available contexts. When the wrapped context exposes `$env`,
   * caller-supplied values such as `$env.row` take precedence for this read. Otherwise the proxy delegates to the
   * original context objects. Registered context namespaces can be evaluated with those temporary values without
   * writing results back to normal state.
   */
  const ENV_CONTEXT = '$env';

  // Registered namespaces need a way back to their owning scope and namespace key when they are read through $withEnv,
  // so the proxy can route property reads to the right item in the registered namespace.
  const registeredNamespaceMap = new WeakMap();

  const hasOwn = (obj, property) => Object.prototype.hasOwnProperty.call(obj, property);

  /**
   * Registers a namespace object that can be re-evaluated when read through `$withEnv(...)`.
   * @param {Object} scope scope that owns the namespace
   * @param {Object} namespaceMap namespace object
   * @param {string} namespace namespace key used to find items in the owning scope
   */
  const registerNamespace = (scope, namespaceMap, namespace) => {
    if (scope && namespaceMap && typeof namespaceMap === 'object' && namespace) {
      registeredNamespaceMap.set(namespaceMap, { scope, namespace });
    }
  };

  /**
   * @param {*} value possible registered namespace object
   * @returns {boolean} true when the object was registered for `$withEnv(...)` reads
   */
  const isRegisteredNamespace = (value) => !!(value && typeof value === 'object' && registeredNamespaceMap.has(value));

  const throwReadOnlyContextError = () => {
    throw new TypeError('$withEnv contexts are read-only.');
  };

  /**
   * @param {Object} handler proxy handler with read traps
   * @returns {Object} proxy handler with shared mutation traps
   */
  const createReadOnlyProxyHandler = (handler) => Object.assign(handler, {
    set: throwReadOnlyContextError,
    defineProperty: throwReadOnlyContextError,
    deleteProperty: throwReadOnlyContextError,
    preventExtensions: throwReadOnlyContextError,
    setPrototypeOf: throwReadOnlyContextError,
  });

  /**
   * @param {*} value context value to inspect
   * @returns {boolean} true when the value can resolve VB variables/constants
   */
  const isVariableContext = (value) => !!(value && typeof value.getVariable === 'function');

  /**
   * @param {*} value context value to inspect
   * @returns {boolean} true when the value is a plain object or array
   */
  const isPlainObjectOrArray = (value) => {
    const prototype = Array.isArray(value) ? undefined : Object.getPrototypeOf(value);
    return Array.isArray(value) || prototype === Object.prototype || prototype === null;
  };

  /**
   * @param {*} value context value to inspect
   * @returns {boolean} true when the value is a VB context object or registered context namespace
   */
  const isContextObject = (value) => !!(value && typeof value === 'object'
    && (isRegisteredNamespace(value) || isVariableContext(value)));

  /**
   * @param {*} value context value to wrap
   * @returns {boolean} true when recursive proxying is safe and useful
   */
  const shouldWrapContextValue = (value) => !!(value && typeof value === 'object'
    && (isContextObject(value) || isPlainObjectOrArray(value)));

  /**
   * Normalizes the optional argument passed to `$withEnv(...)`.
   * @param {*} envOverrides value supplied to `$withEnv(...)`
   * @returns {Object} environment override object
   */
  const normalizeEnvOverrides = (envOverrides) => {
    if (envOverrides === undefined || envOverrides === null) {
      return {};
    }

    if (typeof envOverrides !== 'object' || Array.isArray(envOverrides)) {
      throw new TypeError('$withEnv expects an object.');
    }

    return envOverrides;
  };

  /**
   * Creates the accessor used for the `$env` context exposed through `$withEnv(...)`.
   *
   * When the wrapped context exposes `$env`, those values stay visible inside `$withEnv(...)`; caller-supplied values,
   * currently `$env.row`, win for this temporary read. The merged value is cached for the life of the proxy so multiple
   * `$env` reads during one expression evaluation see the same object.
   * @param {Object} availableContexts current available expression contexts
   * @param {Object} envOverrides environment values supplied to `$withEnv(...)`
   * @returns {Function} accessor returning the merged `$env` object
   */
  const createEnvAccessor = (availableContexts, envOverrides) => {
    let mergedEnv;

    return () => {
      if (!mergedEnv) {
        const currentEnv = (availableContexts && hasOwn(availableContexts, ENV_CONTEXT)
          && availableContexts[ENV_CONTEXT]) || {};
        mergedEnv = Object.assign({}, currentEnv, envOverrides);
      }

      return mergedEnv;
    };
  };

  /**
   * Builds temporary contexts passed into expression evaluation for a `$withEnv(...)` read.
   * @param {Object} availableContexts current available expression contexts
   * @param {Object} envOverrides environment values supplied to `$withEnv(...)`
   * @returns {Object} temporary contexts keyed by context name
   */
  const createExternalContexts = (availableContexts, envOverrides) => ({
    [ENV_CONTEXT]: createEnvAccessor(availableContexts, envOverrides),
  });

  /**
   * Creates a read-only proxy for a registered namespace in a `$withEnv(...)` evaluation.
   *
   * If the namespace is not registered, it is returned unchanged. Registered namespaces are evaluated through the
   * owning scope's rules engine with the `$env` values supplied by `$withEnv(...)`. If a property is evaluated with
   * those temporary values, the proxy returns that result even when no rule produced a value, so stale normal rule
   * results do not leak.
   * @param {Object} namespaceMap namespace object
   * @param {Object} externalContexts temporary contexts to expose while evaluating expressions
   * @param {Function} wrapValue wraps objects/arrays returned from this namespace
   * @returns {Object} namespace proxy for this `$withEnv(...)` read, or the original namespace when not registered
   */
  const createRegisteredNamespaceProxy = (namespaceMap, externalContexts, wrapValue) => {
    const registration = namespaceMap && registeredNamespaceMap.get(namespaceMap);
    if (!registration) {
      return namespaceMap;
    }

    const { scope, namespace } = registration;
    let ruleResults;
    let ruleResultsInitialized = false;

    /**
     * Evaluates rules once for this `$withEnv` namespace proxy.
     * @param {Object} entry item from the registered namespace
     * @returns {Object|undefined} rule results for this proxy's temporary `$env` values
     */
    const getRuleResults = (entry) => {
      if (!entry || typeof entry.isValueFromRules !== 'function' || !entry.isValueFromRules() || !externalContexts) {
        return undefined;
      }

      if (!ruleResultsInitialized) {
        const rulesEngine = scope.container && scope.container.getRulesEngine(false);
        // Evaluate the rules engine for this `$withEnv` read instead of resolving only the requested
        // constant. A constant can depend on rule ordering, same-run values from other rules-backed
        // constants, and fallback defaults. Keep row-specific values out of the normal constants store.
        ruleResults = rulesEngine && rulesEngine.getConstantValuesForExternalContexts(externalContexts);
        ruleResultsInitialized = true;
      }

      return ruleResults;
    };

    /**
     * @param {Object} entry item from the registered namespace
     * @returns {{hasValue: boolean, value: *}|undefined} rules result record for this property
     */
    const getValueForExternalContexts = (entry) => {
      const results = getRuleResults(entry);
      if (results) {
        const rulesEngine = scope.container && scope.container.getRulesEngine(false);
        return rulesEngine && rulesEngine.getConstantValueForExternalContexts(entry, externalContexts, results);
      }
      return undefined;
    };

    return new Proxy(namespaceMap, createReadOnlyProxyHandler({
      get(target, property) {
        if (typeof property !== 'string') {
          return Reflect.get(target, property, target);
        }

        const hasNamespaceProperty = hasOwn(target, property);
        // Avoid treating the namespace proxy as a Promise, but do not hide a real property named "then".
        if (property === 'then' && !hasNamespaceProperty) {
          return undefined;
        }

        if (hasNamespaceProperty) {
          // Check rules-managed entries before reading from the normal namespace; that read can evaluate a lazy
          // default before the `$withEnv` value is consulted.
          const entry = scope.getVariable(property, namespace);
          if (entry) {
            const valueForExternalContexts = getValueForExternalContexts(entry);
            if (valueForExternalContexts !== undefined) {
              return valueForExternalContexts.hasValue ? wrapValue(valueForExternalContexts.value) : undefined;
            }
          }

          const targetValue = Reflect.get(target, property, target);
          const config = Object.getOwnPropertyDescriptor(target, property);
          if (config && config.configurable === false && config.writable === false) {
            return targetValue;
          }

          return wrapValue(targetValue);
        }

        return wrapValue(Reflect.get(target, property, target));
      },
    }));
  };

  /**
   * Wraps an existing context object for use through `$withEnv(...)`.
   *
   * The proxy delegates normal reads to the original object, blocks writes like the normal expression proxy, and wraps
   * registered namespaces so rules can be evaluated with the temporary `$env` values supplied by `$withEnv(...)`.
   * @param {*} value value read from the original context object
   * @param {Object} externalContexts temporary contexts to expose while evaluating expressions
   * @param {WeakMap} cache per-`$withEnv(...)` proxy cache
   * @returns {*} wrapped context value, or the original value for primitives
   */
  const wrapContextValue = (value, externalContexts, cache) => {
    if (!value || typeof value !== 'object' || !shouldWrapContextValue(value)) {
      return value;
    }

    if (cache.has(value)) {
      return cache.get(value);
    }

    if (isRegisteredNamespace(value)) {
      const proxy = createRegisteredNamespaceProxy(value, externalContexts,
        (wrappedValue) => wrapContextValue(wrappedValue, externalContexts, cache));
      cache.set(value, proxy);
      return proxy;
    }

    const proxy = new Proxy(value, createReadOnlyProxyHandler({
      get(target, property) {
        if (typeof property === 'symbol') {
          return target[property];
        }

        // Avoid making proxied context objects look like Promises when expression/binding code probes `.then`.
        if (property === 'then' && !hasOwn(target, property)) {
          return undefined;
        }

        const realValue = Reflect.get(target, property, target);
        const config = Object.getOwnPropertyDescriptor(target, property);
        if (config && config.configurable === false && config.writable === false) {
          return realValue;
        }

        return shouldWrapContextValue(realValue) ? wrapContextValue(realValue, externalContexts, cache) : realValue;
      },
    }));

    cache.set(value, proxy);
    return proxy;
  };

  class WithEnvContext {
    /**
     * Registers a namespace object that can be re-evaluated when read through `$withEnv(...)`.
     *
     * `$withEnv(...)` wraps existing context objects. When a registered namespace is read later, the wrapper only has
     * the namespace object, so this map lets it find the owning scope and route evaluation to the right rules engine.
     * This does not modify the namespace object.
     * @param {Object} scope scope that owns the namespace
     * @param {Object} namespaceMap namespace object
     * @param {string} namespace namespace key used to find items in the owning scope
     */
    static registerNamespace(scope, namespaceMap, namespace) {
      registerNamespace(scope, namespaceMap, namespace);
    }

    /**
     * Creates the context proxy returned by `$withEnv(...)`.
     *
     * The proxy leaves the original available contexts untouched. It overlays `$env`, wraps registered namespaces when
     * they are read, and otherwise delegates to the original contexts.
     * @param {Object} availableContexts current available expression contexts
     * @param {*} envOverrides environment values supplied to `$withEnv(...)`
     * @returns {Object} context proxy for this `$withEnv(...)` call
     */
    static createWithEnvContext(availableContexts, envOverrides) {
      const normalizedEnvOverrides = normalizeEnvOverrides(envOverrides);
      const externalContexts = createExternalContexts(availableContexts, normalizedEnvOverrides);
      const cache = new WeakMap();

      return new Proxy(availableContexts, createReadOnlyProxyHandler({
        get(target, property) {
          if (typeof property === 'symbol') {
            return target[property];
          }

          if (property === ENV_CONTEXT) {
            return wrapContextValue(externalContexts[ENV_CONTEXT](), externalContexts, cache);
          }

          const realValue = Reflect.get(target, property, target);
          return shouldWrapContextValue(realValue) ? wrapContextValue(realValue, externalContexts, cache) : realValue;
        },
      }));
    }
  }

  return WithEnvContext;
});

